<template>
  <div ref="categorySection" :class="$style.categorySection">
    <!-- 主分类区域 -->
    <div ref="mainCategories" :class="$style.mainCategories">
      <div
        v-for="category in categories"
        :key="category.id"
        :ref="`categoryItem-${category.id}`"
        :class="[
          $style.categoryItem,
          { [$style.active]: activeCategory === category.id },
        ]"
        @click="handleCategoryClick(category)"
      >
        <div :class="$style.categoryIcon">
          <van-image
            :src="category.categoryPic"
            :alt="category.categoryName"
            lazy-load
            round
            fit="cover"
          />
        </div>
        <div :class="$style.categoryName">{{ category.categoryName }}</div>
      </div>
    </div>

    <!-- 展开按钮 -->
    <div :class="$style.expandButton" @click="togglePopup">
      <div :class="$style.expandText">展开</div>
      <x-icon :class="$style.expandIcon" name="caret-down" />
    </div>

    <!-- 自定义弹出层 -->
    <transition name="popup" @enter="onEnter" @leave="onLeave">
      <div
        v-if="showPopup"
        :class="$style.popupOverlay"
        :style="overlayStyle"
        @click="closePopup"
      >
        <div :class="$style.popupContent" @click.stop>
          <!-- 所有分类 -->
          <div :class="$style.allCategories">
            <div
              v-for="category in categories"
              :key="category.id"
              :class="[
                $style.categoryItem,
                { [$style.active]: activeCategory === category.id },
              ]"
              @click="handleCategoryClick(category)"
            >
              <div :class="$style.categoryIcon">
                <van-image
                  :src="category.categoryPic"
                  :alt="category.categoryName"
                  lazy-load
                  round
                  fit="cover"
                />
              </div>
              <div :class="$style.categoryName">
                {{ category.categoryName }}
              </div>
            </div>
          </div>

          <!-- 收起按钮 -->
          <div :class="$style.collapseButton" @click="closePopup">
            <div :class="$style.collapseText">收起</div>
            <x-icon :class="$style.collapseIcon" name="caret-up" />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
  import Vue from 'vue'

  export default Vue.extend({
    name: 'CategorySection',

    props: {
      categories: {
        type: Array,
        default: () => [],
      },
      defaultActive: {
        type: [String, Number],
        default: null,
      },
    },

    data() {
      return {
        showPopup: false,
        activeCategory: null,
        overlayStyle: {},
      }
    },

    watch: {
      defaultActive(newVal) {
        this.activeCategory = newVal

        // 初始滚动到选中项
        this.$nextTick(() => {
          this.scrollToActiveCategory()
        })
      },
    },

    methods: {
      togglePopup() {
        this.showPopup = !this.showPopup
        if (this.showPopup) {
          this.$nextTick(() => {
            this.calculateOverlayStyle()
          })
        }
      },

      closePopup() {
        this.showPopup = false
      },

      handleCategoryClick(category) {
        this.activeCategory = category.id
        this.closePopup()

        // 滚动到选中的分类
        this.$nextTick(() => {
          this.scrollToActiveCategory()
        })

        // 触发分类切换事件
        this.$emit('change', category)
      },

      // 滚动到选中的分类项
      scrollToActiveCategory() {
        if (!this.activeCategory || !this.$refs.mainCategories) return

        const activeElement = this.$refs[`categoryItem-${this.activeCategory}`]
        if (!activeElement || !activeElement[0]) return

        const container = this.$refs.mainCategories
        const element = activeElement[0]

        // 计算元素相对于容器的位置
        const elementLeft = element.offsetLeft
        const elementWidth = element.offsetWidth
        const containerWidth = container.clientWidth

        // 计算目标滚动位置（居中显示）
        const targetScrollLeft =
          elementLeft - (containerWidth - elementWidth) / 2

        // 平滑滚动
        container.scrollTo({
          left: Math.max(0, targetScrollLeft),
          behavior: 'smooth',
        })
      },

      // 计算遮罩层样式，确保覆盖到页面底部
      calculateOverlayStyle() {
        if (!this.$refs.categorySection) return

        const rect = this.$refs.categorySection.getBoundingClientRect()
        const viewportHeight = window.innerHeight
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop

        // 计算从组件底部到页面底部的高度
        const componentBottom = rect.bottom + scrollTop
        const documentHeight = Math.max(
          document.body.scrollHeight,
          document.documentElement.scrollHeight,
          viewportHeight,
        )

        const overlayHeight = documentHeight - componentBottom + rect.height

        this.overlayStyle = {
          height: `${overlayHeight}px`,
          maxHeight: `${overlayHeight}px`,
        }
      },

      // 防止页面滚动
      onEnter() {
        document.body.style.overflow = 'hidden'
        document.documentElement.style.overflow = 'hidden'
      },

      onLeave() {
        document.body.style.overflow = ''
        document.documentElement.style.overflow = ''
      },
    },
  })
</script>

<style lang="scss" module>
  .categorySection {
    background-color: #fff;
    box-sizing: border-box;
    box-shadow: inset 0px -1px 0px 0px rgba(111, 111, 111, 0.1);
    padding: 12px 40px 12px 5px;
    position: relative;
    min-height: 95px;
    flex-shrink: 0;

    // 隐藏滚动条
    ::-webkit-scrollbar {
      display: none;
    }

    // 兼容其他浏览器
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .mainCategories {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    overflow-x: auto;
    gap: 16px;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .categoryItem {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    transition: all 0.3s ease;
    cursor: pointer;
    flex-shrink: 0;

    .categoryIcon {
      border: 2px solid transparent;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: #fff2e6;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      overflow: hidden;
    }

    .categoryName {
      border-radius: 20px;
      color: #666666;
      padding: 2px 7px;
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      white-space: nowrap;
    }

    &.active {
      .categoryIcon {
        border-color: #ffad5c;
      }

      .categoryName {
        background-color: #ff840a;
        color: #fff;
      }
    }
  }

  .expandButton {
    position: absolute;
    top: 12.5px;
    right: 0;
    bottom: 12.5px;
    padding: 8px;
    z-index: 10;
    background-color: #fff;
    box-shadow: -1px 0px 4px 0px rgba(0, 0, 0, 0.1);
    color: #333;
    display: flex;
    font-size: 13px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &::before,
    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      height: 12px;
      background-color: #fff;
    }

    &::before {
      top: -12px;
    }

    &::after {
      bottom: -12px;
    }

    .expandText {
      writing-mode: vertical-lr;
    }

    .expandIcon {
      width: 13px;
    }
  }

  // 自定义弹出层样式
  .popupOverlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
    overflow: hidden;
  }

  .popupContent {
    background: #fff;
    width: 100%;
    border-radius: 0 0 12px 12px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    max-height: 70vh;
  }

  .allCategories {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    padding: 16px;
    flex: 1;
    overflow: hidden;
    min-height: 0;
  }

  .collapseButton {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px;
    border-top: 1px solid #e9e9e9;
    font-size: 13px;
    color: #333;
    cursor: pointer;
    flex-shrink: 0;
  }

  .collapseText {
    font-size: 14px;
  }

  .collapseIcon {
    font-size: 12px;
  }

  // 弹出动画
  :global(.popup-enter-active),
  :global(.popup-leave-active) {
    transition: all 0.3s ease;
  }

  :global(.popup-enter-active) .popupContent,
  :global(.popup-leave-active) .popupContent {
    transition: transform 0.3s ease;
  }

  :global(.popup-enter),
  :global(.popup-leave-to) {
    opacity: 0;
  }

  :global(.popup-enter) .popupContent,
  :global(.popup-leave-to) .popupContent {
    transform: translateY(-100%);
  }
</style>
